import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, mapTo, switchMap } from "rxjs/operators";
import { SubmissionsFacade } from "../../../../domain/+state/submissions.facade";
import { SubmissionDetailsFacade } from "../../submission-details.facade";
import { SubmissionDetailsBaseComponentData } from "../../submission-details.model";
import { getSelectedOfferBundle } from "../../submission-details.data";
import { ClosingSubmissionAdapter, getClosingDetailsAppBarActions, getClosingDetailsHeaderData, getIncompleteSubmissionDialogData, getSubmissionDetailsBannerData, getSubmitConfirmationDialogData } from "./closing-details.data";
import { ClosingDetailsComponentData } from "./closing-details.model";
import { ApplicationStage, OfferBundleCheckoutRequirement, AdvisorData, SimpleDialogComponent, SimpleDialogData } from "common";
import { MatDialog } from "@angular/material/dialog";
import { ApplicationData } from "../../../../domain/models/application.model";
import { BrokerStatus } from "@partner/shared/SharedConstants";
import { DownloadRequirementDocumentData, DeleteRequirementDocumentData, SaveRequirementDocumentData } from "../../../../domain/models/submissions.model";
import { OfferDetailsSectionAdapter } from "../../shared/modules/offer-details-section/offer-details-section.data";

@Injectable()
export class ClosingDetailsFacade {
  constructor(
    private readonly detailsFacade: SubmissionDetailsFacade,
    private readonly submissionsFacade: SubmissionsFacade,
    private readonly closingSubmissionAdapter: ClosingSubmissionAdapter,
    private readonly offerDetailsSectionAdapter: OfferDetailsSectionAdapter,
    private readonly dialog: MatDialog,
  ) { }

  getComponentData$(applicationId: number): Observable<ClosingDetailsComponentData> {
    return this.detailsFacade.getComponentBaseData$(applicationId)
      .pipe(switchMap((data: SubmissionDetailsBaseComponentData) => {
        return this.getClosingDetailsComponentData$(data);
      }));
  }

  private getClosingDetailsComponentData$(data: SubmissionDetailsBaseComponentData): Observable<ClosingDetailsComponentData> {
    return this.submissionsFacade.getAdvisorData$(data.application.id)
      .pipe(switchMap((advisorData: AdvisorData) => {
        const selectedOfferBundleId = getSelectedOfferBundle(data.application)?.id;
        const viewData = {
          ...data,
          headerData: getClosingDetailsHeaderData(data.application.brokerStatus, advisorData),
          advisorData,
          offerDetailsData: this.offerDetailsSectionAdapter.adaptOfferDetailsSectionData(data.application),
          bannerData: getSubmissionDetailsBannerData(data.application),
          appBarActions: getClosingDetailsAppBarActions(data.application.brokerStatus),
        }

        if (!selectedOfferBundleId) {
          return of(viewData);
        }
        return this.submissionsFacade.getCheckoutRequirements$(selectedOfferBundleId)
          .pipe(map((checkoutRequirements: OfferBundleCheckoutRequirement[]) => ({
            ...viewData,
            selectedOfferBundleId: <number>selectedOfferBundleId,
            offerRequirementsData: this.closingSubmissionAdapter.adaptOfferRequirementsSectionData(data.application, checkoutRequirements),
          })))
      }))
  }

  openIncompleteSubmissionDialogIfNeeded(application: ApplicationData): Observable<string> {
    if (application.brokerStatus !== BrokerStatus.CLOSING_INCOMPLETE) {
      return;
    }

    const dialogData: SimpleDialogData = getIncompleteSubmissionDialogData(application.incompleteInfo);
    return SimpleDialogComponent.show(this.dialog, dialogData)
  }

  removeCheckoutRequirementDocument(offerBundleId: number, data: DeleteRequirementDocumentData): void {
    this.submissionsFacade.removeCheckoutRequirementDocument(offerBundleId, data).subscribe();
  }

  downloadCheckoutRequirementDocument(data: DownloadRequirementDocumentData): void {
    this.submissionsFacade.downloadCheckoutRequirementDocument(data);
  }

  saveCheckoutRequirementDocuments(offerBundleId: number, documentsToSave: SaveRequirementDocumentData[]): void {
    this.submissionsFacade.saveCheckoutRequirementDocuments(offerBundleId, documentsToSave).subscribe();
  }

  submitForReview(appId: number, advisorData: AdvisorData): Observable<void> {
    return this.submissionsFacade.updateApplicationData(appId, { stage: ApplicationStage.ClosingReview })
      .pipe(switchMap(() => this.openConfirmationDialog(advisorData)));
  }

  private openConfirmationDialog(advisorData: AdvisorData): Observable<void> {
    const dialogData: SimpleDialogData = getSubmitConfirmationDialogData(advisorData);
    return SimpleDialogComponent.show(this.dialog, dialogData).pipe(mapTo(undefined));
  }
}
